import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiRequestError, search } from "../api";
import { checkIsin, normalizeIsin } from "../lib/isin";
import { useServiceStatus } from "../lib/status";

const ISIN_ES = "https://isin-es.h1756.es";

const INVALID = "Invalid ISIN: check the 12 characters";

type Outcome =
  | { kind: "invalid" }
  | { kind: "elsewhere"; isin: string }
  | { kind: "unknown"; q: string }
  | { kind: "error" };

/** ISIN input with instant structure/check-digit feedback, then a
 * lookup that never leaves the user on the home page: a passport
 * here, the isin-es record, or an honest "nothing knows this". */
export function IsinSearch({ size = "lg" }: { size?: "sm" | "lg" }) {
  const [q, setQ] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const nav = useNavigate();
  const st = useServiceStatus();
  const hintId = useId();
  const res = checkIsin(q);
  const showError = touched && q.length > 0 && !res.ok;
  const demo = st.data?.demo;

  /** A demo instance only serves the passports it captured. */
  const hasPassport = (isin: string) =>
    !demo ||
    demo.corpus.some((c) => c.isin === isin) ||
    (demo.partial ?? []).some((c) => c.isin === isin);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setOutcome(null);
    if (!res.ok) {
      setOutcome({ kind: "invalid" });
      return;
    }
    const isin = res.isin;
    if (hasPassport(isin)) {
      setQ("");
      setTouched(false);
      nav(`/isin/${isin}`);
      return;
    }
    setBusy(true);
    try {
      const found = await search(isin);
      if (found.results.some((r) => r.isin === isin)) {
        setOutcome({ kind: "elsewhere", isin });
      } else {
        setOutcome({ kind: "unknown", q: isin });
      }
    } catch (err) {
      setOutcome(
        err instanceof ApiRequestError && err.code === "INVALID_QUERY"
          ? { kind: "invalid" }
          : { kind: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  const sm = size === "sm";
  return (
    <form onSubmit={submit} className={`relative ${sm ? "w-full" : "w-full max-w-xl"}`} noValidate>
      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e) => {
            setQ(normalizeIsin(e.target.value).slice(0, 12));
            if (normalizeIsin(e.target.value).length >= 12) setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          placeholder={sm ? "Search ISIN" : "Enter an ISIN, e.g. DE000A3LJCB4"}
          maxLength={14}
          autoFocus={!sm}
          spellCheck={false}
          autoComplete="off"
          aria-label={sm ? "Search ISIN" : "ISIN"}
          aria-invalid={showError}
          aria-describedby={showError ? hintId : undefined}
          className={`mono min-w-0 flex-1 rounded-md border bg-[var(--surface-2)] tracking-wider outline-none placeholder:tracking-normal placeholder:text-[var(--ink-3)] focus:border-[var(--accent)] ${
            showError ? "border-rose-500/60" : "border-[var(--line)]"
          } ${sm ? "h-8 px-2.5 text-[12.5px]" : "h-12 px-4 text-[15px]"}`}
        />
        {sm ? null : (
          <button
            type="submit"
            disabled={busy}
            className="h-12 shrink-0 whitespace-nowrap rounded-md bg-[var(--accent)] px-6 text-[14px] font-medium text-white transition hover:brightness-110"
          >
            {busy ? "Searching…" : "Build passport"}
          </button>
        )}
      </div>
      {showError && !res.ok ? (
        <p id={hintId} role="alert" className={`mt-1.5 text-rose-600 dark:text-rose-400 ${sm ? "absolute text-[11px]" : "text-[12.5px]"}`}>
          {res.reason}
        </p>
      ) : null}
      {outcome ? (
        <p role="status" className={`mt-2 text-[var(--ink-2)] ${sm ? "text-[11px]" : "text-[12.5px]"}`}>
          <OutcomeText outcome={outcome} />
        </p>
      ) : null}
    </form>
  );
}

function OutcomeText({ outcome }: { outcome: Outcome }) {
  if (outcome.kind === "invalid") return <>{INVALID}</>;
  if (outcome.kind === "error")
    return <>Search is unavailable right now; try the passport page directly.</>;
  if (outcome.kind === "elsewhere")
    return (
      <>
        Known to isin-es (ESMA FIRDS) but no passport here yet —{" "}
        <a
          href={`${ISIN_ES}/isin/${outcome.isin}`}
          className="text-[var(--accent)] underline underline-offset-2"
        >
          open it on isin-es →
        </a>
      </>
    );
  return (
    <>
      No public source connected to this site knows this ISIN —{" "}
      <a
        href={`${ISIN_ES}/buscar?q=${encodeURIComponent(outcome.q)}`}
        className="text-[var(--accent)] underline underline-offset-2"
      >
        search isin-es →
      </a>
    </>
  );
}
